import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ContentApi } from "../../../shared/api/contentApi";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import DetailPageHero from "../../../components/ui/DetailPageHero";
import DetailTabs, { DetailTabsContent } from "../../../components/ui/DetailTabs";
import DetailPageSidebar from "../../../components/ui/DetailPageSidebar";
import { Info, Database } from "lucide-react";
import { notify } from "shared/utils/notify";

import formatDate from "../../../shared/helpers/formatDate";
import CreateVersionDialog from "../components/CreateVersionDialog.jsx";
import ProjectEditDialog from "../components/EditProjectDialog.jsx";
import { Modal, Flex, Card, Button, Splitter } from "antd";
import { DETAIL_CARD_CLASS } from "../../../shared/constants/detailLayout";
import EditStrings from "../../content/_shared/EditStrings";


export default function ProjectDetails() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [createVersionOpen, setCreateVersionOpen] = useState(false);
    const [editProjectOpen, setEditProjectOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedVersionId, setSelectedVersionId] = useState(null);

    const loadDetails = async (signal) => {
        if (!projectId) {
            setProject(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError("");
        try {
            const [projectData, versionsData] = await Promise.all([
                ContentApi.getProjectById(projectId, { signal }),
                ContentApi.getVersions({ projectId: projectId, page: 1, pageSize: 1000 }, { signal }),
            ]);
            setProject(projectData || null);
            setVersions(Array.isArray(versionsData?.items) ? versionsData.items : []);
        } catch (e) {
            if (!signal?.aborted) {
                setError(e?.message || "Ошибка загрузки");
            }
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    };

    useEffect(() => {
        const ac = new AbortController();
        loadDetails(ac.signal);

        return () => ac.abort();
    }, [projectId]);

    const handleDelete = async () => {
        try {
            await ContentApi.deleteProject(projectId);
            notify.success("Проект удалён");
            navigate("/projects/list");
        } catch (err) {
            notify.error(`Ошибка удаления: ${err.message}`);
        }
    };

    const sortedVersions = useMemo(() => {
        return [...versions].sort((left, right) => {
            const leftTime = Date.parse(left?.updatedAt || 0) || 0;
            const rightTime = Date.parse(right?.updatedAt || 0) || 0;
            return rightTime - leftTime;
        });
    }, [versions]);

    const selectedVersion = useMemo(() => {
        return sortedVersions.find((version) => String(version.id) === selectedVersionId)
            || sortedVersions[0]
            || null;
    }, [selectedVersionId, sortedVersions]);

    const tabs = [
        { value: "overview", label: "Обзор", icon: <Info size={16} /> },
        { value: "usage", label: "Использование", icon: <Database size={16} /> },
        { value: "meta", label: "Метаданные", icon: <Info size={16} /> },
    ];

    if (loading) {
        return (
            <section className="p-4 md:p-6">
                <div className="p-6 text-sm text-zinc-500">Загрузка…</div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="p-4 md:p-6">
                <div className="rounded-xl border border-red-300 bg-red-50 text-red-700 p-3 text-sm">
                    {error}
                </div>
            </section>
        );
    }

    if (!project) {
        return (
            <section className="p-4 md:p-6">
                <div className="p-6 text-sm text-zinc-500">Не найдено</div>
            </section>
        );
    }

    return (
        <section className="p-4 md:p-6">
            <div className="mx-auto w-full max-w-6xl">
                <Flex vertical gap="large" className="mt-2 w-full">
                    <div className="w-full">
                        <DetailTabs defaultValue="overview" tabs={tabs}>
                            {/* Overview Tab */}
                            <DetailTabsContent value="overview" className="space-y-4">
                                <DetailPageHero
                                    title={project.name}
                                    subtitle={project.systemName}
                                    breadcrumbs={[
                                        { label: "Проекты", to: "/projects/list" },
                                        { label: "Детали" },
                                    ]}
                                    metadata={[
                                        { label: "ID", value: `#${project.id}` },
                                        { label: "Версии", value: String(sortedVersions.length) },
                                    ]}
                                    onEdit={() => setEditProjectOpen(true)}
                                    onDelete={() => setDeleteDialogOpen(true)}
                                />

                                <EditStrings
                                    projectId={projectId}
                                    versionId={null}
                                    entityId={project.id}
                                    entity={project}
                                    onSaved={(updated) => {
                                        setProject(updated || project);
                                        notify.success("Изменения сохранены");
                                    }}
                                    title="Редактирование строк проекта"
                                >
                                </EditStrings>
                                
                                <Card title="Описание" className={DETAIL_CARD_CLASS}>
                                    <h2 className="eyebrow mb-2">Описание</h2>
                                    {project.description ? (
                                        <p className="text-sm">{project.description}</p>
                                    ) : (
                                        <p className="text-sm text-zinc-500">Нет описания</p>
                                    )}
                                </Card>
                                <Card title="Дополнительно" className={DETAIL_CARD_CLASS}>
                                    <div>
                                        <h2 className="eyebrow">Статус в архиве</h2>
                                        {project.isArchived ? (
                                            <p className="text-sm">{project.isArchived ?? "Да"}</p>
                                        ) : (
                                            <p className="text-sm text-zinc-500">Нет</p>
                                        )}
                                    </div>

                                    <div className="mt-4">
                                        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-zinc-500">
                                            <div>Создана: {project?.createdAt ? formatDate(project.createdAt) : "-"}</div>
                                            <div>
                                                Обновлено: {project?.updatedAt ? formatDate(project.updatedAt) : project?.createdAt ? formatDate(project.createdAt) : "-"}
                                            </div>
                                        </div>
                                        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-zinc-500">
                                            <div>Создана кем: {project?.createdBy ?? "-"}</div>
                                            <div>
                                                Обновлено кем: {project?.updatedBy ?? "-"}
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </DetailTabsContent>

                            <DetailTabsContent value="usage" className="space-y-4">
                                <Splitter style={{ boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)' }} >
                                    <Splitter.Panel collapsible min="20%">
                                        <div title="Используемые версии" className="p-2">
                                            <Flex align="center" justify="space-between" gap="small" wrap className="mb-3">
                                                <h2 className="text-xl">Все версии в проекте:</h2>
                                                <Button type="primary" onClick={() => setCreateVersionOpen(true)}>
                                                    Создать версию
                                                </Button>
                                            </Flex>

                                            {sortedVersions.length === 0 ? (
                                                <p className="text-sm text-zinc-500">У проекта пока нет версий.</p>
                                            ) : (
                                                <div className="space-y-2">
                                                    {sortedVersions.map((version) => {
                                                        const isSelected = version.id === selectedVersion?.id;

                                                        return (
                                                            <button
                                                                type="button"
                                                                key={version.id}
                                                                aria-pressed={isSelected}
                                                                onClick={() => setSelectedVersionId(String(version.id))}
                                                                className={`w-full rounded-lg border p-3 text-left transition-colors ${isSelected
                                                                    ? "border-blue-500 bg-blue-50/50 dark:bg-blue-950/20"
                                                                    : "border-zinc-200 hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800/50"
                                                                    }`}
                                                            >
                                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                                    <div>
                                                                        <div className="text-sm font-medium">
                                                                            {version.name || version.systemName || `#${version.id}`}
                                                                        </div>
                                                                        <div className="text-xs text-zinc-500">ID: {version.id}</div>
                                                                    </div>
                                                                    <span className="chip chip-muted">{version.status || "UNKNOWN"}</span>
                                                                </div>
                                                                <div className="mt-2 text-xs text-zinc-500">
                                                                    <div>Номер: {version.number ?? "-"}</div>
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </Splitter.Panel>
                                    <Splitter.Panel collapsible style={{ borderRadius: "1rem", }}>
                                        {selectedVersion ? (
                                            <Card
                                                title={selectedVersion.name || selectedVersion.systemName || `Версия #${selectedVersion.id}`}
                                                className="h-full w-full"
                                            >
                                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                    <div>
                                                        <dt className="eyebrow">System Name</dt>
                                                        <dd className="mt-0.5 break-words">{selectedVersion.systemName || "-"}</dd>
                                                    </div>
                                                    <div>
                                                        <dt className="eyebrow">Статус</dt>
                                                        <dd className="mt-0.5">
                                                            <span className="chip chip-muted">{selectedVersion.status || "UNKNOWN"}</span>
                                                        </dd>
                                                    </div>
                                                    <div>
                                                        <dt className="eyebrow">ID</dt>
                                                        <dd className="mt-0.5 break-all">{selectedVersion.id}</dd>
                                                    </div>
                                                    <div>
                                                        <dt className="eyebrow">Номер версии</dt>
                                                        <dd className="mt-0.5">{selectedVersion.number ?? "-"}</dd>
                                                    </div>
                                                </div>

                                                <div className="mt-5">
                                                    <dt className="eyebrow">Описание</dt>
                                                    <dd className="mt-1 whitespace-pre-wrap text-sm">
                                                        {selectedVersion.description || "Нет описания у данной версии"}
                                                    </dd>
                                                </div>

                                                <div className="mt-5">
                                                    <dt className="eyebrow">Комментарий</dt>
                                                    <dd className="mt-1 whitespace-pre-wrap text-sm text-zinc-500">
                                                        {selectedVersion.comment || "-"}
                                                    </dd>
                                                </div>

                                                <div className="mt-5 grid grid-cols-1 gap-2 text-xs text-zinc-500 sm:grid-cols-2">
                                                    <div>Создана: {selectedVersion.createdAt ? formatDate(selectedVersion.createdAt) : "-"}</div>
                                                    <div>
                                                        Обновлено: {selectedVersion.updatedAt
                                                            ? formatDate(selectedVersion.updatedAt)
                                                            : selectedVersion.createdAt
                                                                ? formatDate(selectedVersion.createdAt)
                                                                : "-"}
                                                    </div>
                                                    <div>Создана кем: {selectedVersion.createdBy ?? "-"}</div>
                                                    <div>Обновлено кем: {selectedVersion.updatedBy ?? "-"}</div>
                                                </div>
                                            </Card>
                                        ) : (
                                            <Flex justify="center" align="center" style={{ height: "100%" }}>
                                                <p className="text-sm text-zinc-500">Выберите версию для просмотра деталей.</p>
                                            </Flex>
                                        )}
                                    </Splitter.Panel>
                                </Splitter>
                            </DetailTabsContent>
                            <DetailTabsContent value="meta" className="space-y-4">
                                <Card className={DETAIL_CARD_CLASS}>
                                    <div className="">
                                        <dt className="eyebrow">Версий</dt>
                                        <dd className="mt-0.5">{sortedVersions.length}</dd>
                                    </div>
                                    <div className="mt-4">
                                        <dt className="eyebrow">Id</dt>
                                        <dd className="mt-0.5">{project?.id}</dd>
                                    </div>
                                    <div className="mt-4">
                                        <dt className="eyebrow">System Name</dt>
                                        <dd className="mt-0.5">{project?.systemName}</dd>
                                    </div>
                                    <div className="mt-4">
                                        <dt className="eyebrow">Архивировано</dt>
                                        <dd className="mt-0.5">{project?.isArchive ? "Да" : "Нет"}</dd>
                                    </div>
                                    <div className="mt-4">
                                        <dt className="eyebrow">Создан</dt>
                                        <dd className="mt-0.5">{formatDate(project?.createdAt)}</dd>
                                    </div>
                                    <div className="mt-4">
                                        <dt className="eyebrow">Создан кем</dt>
                                        <dd className="mt-0.5">{project?.createdBy}</dd>
                                    </div>
                                </Card>
                            </DetailTabsContent>
                        </DetailTabs>
                    </div>
                </Flex>

                <ProjectEditDialog
                    open={editProjectOpen}
                    item={project}
                    onClose={() => setEditProjectOpen(false)}
                    onSaved={async (updated) => {
                        setProject(updated || project);
                        await loadDetails();
                        setEditProjectOpen(false);
                    }}
                />

                <CreateVersionDialog
                    open={createVersionOpen}
                    onClose={() => setCreateVersionOpen(false)}
                    projectId={projectId}
                    versions={sortedVersions}
                    onCreated={async () => {
                        notify.success("Версия создана");
                        const ac = new AbortController();
                        await loadDetails(ac.signal);
                    }}
                />

                {/* Delete Confirmation Dialog */}
                <Modal
                    open={deleteDialogOpen}
                    onCancel={() => setDeleteDialogOpen(false)}
                    title="Удалить сущность?"
                    closable={false}
                    centered
                    width=""
                    className=""
                    styles={{
                        body: { padding: 0 },
                        content: { overflow: "hidden" },
                    }}
                    onOk={() => { handleDelete(); setDeleteDialogOpen(false); }}
                    footer={(_, { OkBtn, CancelBtn }) => (
                        <>
                            <OkBtn />
                            <CancelBtn />
                        </>
                    )}
                >
                    <div className="text-sm text-zinc-600 dark:text-zinc-400 mb-2">
                        Внимание! Удаление проекта может привести к ошибкам, которые его используют.
                    </div>
                    <p>
                        <strong>Project:</strong> {project?.name}
                    </p>
                    <p className="mt-1">
                        <strong>System Name:</strong> {project?.systemName}
                    </p>
                </Modal>
            </div>
        </section>
    );
}